/**
 * /billing/kitchen — CHEF_ADMIN payout view (Plan 20).
 *
 * Renders inline from BillingPage (the parent supplies the branching
 * switcher + the page title); this module owns the next-payout card +
 * history table + detail drawer.
 *
 * Permission gate (Plan 20 acceptance #3 + #7):
 *   - CHEF_ADMIN at ≥1 vendor org → page renders normally.
 *   - CHEF_STAFF without CHEF_ADMIN → server returns `permittedVendors: []`,
 *     page renders a 403-style hint asking them to contact their chef
 *     admin. Same posture as Plan 14's "No permitted schools" state.
 *
 * Loading state is the block-for-block `BillingKitchenSkeleton`.
 * Pages never render a "Loading…" string per CLAUDE.md.
 *
 * Multi-kitchen rollup: when a chef admins >1 kitchen organization,
 * the page picks the first alphabetically by default. A vendor-switch
 * filter chip is a Plan-20 Non-Goal — documented as future polish.
 */

import { Card, CardContent, CardHeader } from '@ordo/ui/card'
import { toast } from '@ordo/ui/sonner'
import { useEffect, useState } from 'react'

import { type KitchenPayoutRow, useBillingKitchenSummaryQuery } from '@/api/billing-kitchen'
import { NextPayoutCard } from '@/components/billing/kitchen/next-payout-card'
import { PayRatesPanel } from '@/components/billing/kitchen/pay-rates-panel'
import { PayoutDetailDrawer } from '@/components/billing/kitchen/payout-detail-drawer'
import { PayoutHistoryTable } from '@/components/billing/kitchen/payout-history-table'
import { BillingKitchenSkeleton } from '@/skeletons/billing-kitchen-skeleton'

export function KitchenPayoutsPage() {
  const { data, isPending, error, refetch } = useBillingKitchenSummaryQuery({})

  const [selectedRow, setSelectedRow] = useState<KitchenPayoutRow | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    if (error) {
      toast.error("Couldn't load kitchen payouts", {
        description: error.message,
        action: {
          label: 'Retry',
          onClick: () => {
            void refetch()
          },
        },
      })
    }
  }, [error, refetch])

  if (isPending && !data) {
    return <BillingKitchenSkeleton />
  }

  if (!data || data.permittedVendors.length === 0) {
    return <NoPayoutAccessState />
  }

  const activeVendor =
    data.permittedVendors.find((v) => v.organizationId === data.activeVendorId) ?? data.permittedVendors[0] ?? null

  const handleSelectRow = (row: KitchenPayoutRow) => {
    setSelectedRow(row)
    setDrawerOpen(true)
  }

  // The upcoming envelope opens the SAME drawer the history rows do, seeded
  // with the detail already in `data.nextPayout` — so it costs no extra fetch.
  // It used to be the only envelope with no per-item view at all, which is
  // exactly the one a chef most wants itemised.
  const handleViewNextPayout = () => {
    if (data.nextPayout) {
      setSelectedRow(data.nextPayout.payout)
      setDrawerOpen(true)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {activeVendor ? (
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          Kitchen · {activeVendor.organizationName}
        </p>
      ) : null}

      <NextPayoutCard payout={data.nextPayout} onViewDetail={handleViewNextPayout} />

      <Card>
        <CardHeader className="flex flex-col gap-1.5">
          <h3 className="text-lg font-semibold tracking-tight">Payout history</h3>
          <p className="text-xs text-muted-foreground">
            Past payouts and their Tipalti reconciliation status. Click a row to see the line breakdown.
          </p>
        </CardHeader>
        <CardContent>
          <PayoutHistoryTable rows={data.history} onSelect={handleSelectRow} />
        </CardContent>
      </Card>

      {/* Phase E — read-only per-item chef pay + scheduled rate changes. */}
      <PayRatesPanel servicingVendorId={data.activeVendorId} />

      <PayoutDetailDrawer
        open={drawerOpen}
        payoutId={selectedRow?.id ?? null}
        preloadRow={selectedRow}
        preloadDetail={data.nextPayout}
        onOpenChange={(next) => {
          setDrawerOpen(next)
          if (!next) {
            // Defer clearing the row so the drawer close animation
            // still has the header content while it slides out.
            setTimeout(() => setSelectedRow(null), 200)
          }
        }}
      />
    </div>
  )
}

function NoPayoutAccessState() {
  return (
    <Card className="p-8">
      <h2 className="text-lg font-medium">No payout access</h2>
      <p className="mt-2 max-w-xl text-sm text-muted-foreground">
        Ask your chef admin to grant you payout access. CHEF_ADMIN is required to view kitchen payouts; CHEF_STAFF
        members do not see this surface in v1.
      </p>
    </Card>
  )
}
